import {
  IconAnalytics,
  IconBell,
  IconBinocularsGray,
  IconCar,
  IconCctvLine,
  IconChainLink,
  IconChartBar,
  IconChartBarGray,
  IconChartRadius,
  IconClockGray,
  IconCloudCheckGray,
  IconDashboard,
  IconLayers,
  IconLocation,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconSecurity,
  IconTagGray,
  IconTvocGray,
  IconWarning,
  IconZoneGray,
} from '@assets/images/icon';
import { rtlsSystem } from '@assets/images/solution';
import mainVideo from '@src/assets/images/solution/constructionSafety/video/rtlsVideo.mp4';
import subVideo1 from '@src/assets/images/solution/constructionSafety/video/sub/rtlsVideo1.mp4';
import subVideo2 from '@src/assets/images/solution/constructionSafety/video/sub/rtlsVideo2.mp4';
import subVideo3 from '@src/assets/images/solution/constructionSafety/video/sub/rtlsVideo3.mp4';
import subVideo4 from '@src/assets/images/solution/constructionSafety/video/sub/rtlsVideo4.mp4';
import subVideo5 from '@src/assets/images/solution/constructionSafety/video/sub/rtlsVideo5.mp4';

import image1 from '@src/assets/images/solution/constructionSafety/img/1.avif';
import image2 from '@src/assets/images/solution/constructionSafety/img/2.avif';
import image3 from '@src/assets/images/solution/constructionSafety/img/3.avif';
import image4 from '@src/assets/images/solution/constructionSafety/img/4.avif';

export default {
  type: 'Construction Solution',
  banner: {
    title: 'Detect and respond to worker risks on construction sites instantly.',
    desc: 'Monitor the real-time location of workers and respond immediately to hazards to strengthen site safety.',
  },

  video: {
    title: 'Optimize construction site safety management with real-time location data.',
    desc: 'Accurately track the real-time location of site workers to identify risks in advance and respond quickly, enhancing both safety and operational efficiency.',
    play: mainVideo,
  },

  func: [
    {
      theme: 'Monitor workers’ locations in real time to minimize accident risks.',
      title: 'Real-Time Worker Location Tracking',
      desc: 'Precisely monitor workers’ current positions within ±50cm in real time, instantly detecting potential hazards on site. This enables immediate response and preventative measures.',
      sub1: 'High Accuracy',
      icon1: IconRulerGray,
      sub2: 'Real-Time Monitoring',
      icon2: IconClockGray,
      video: subVideo1,
    },
    {
      title: 'Automatic Alerts for Restricted Area Entry',
      desc: 'When a worker approaches or enters a designated danger zone, managers receive instant alerts. This ensures worker safety and helps prevent potential accidents.',
      sub1: 'Real-Time Location Tracking',
      icon1: IconBinocularsGray,
      sub2: 'Fast Data Integration',
      icon2: IconTvocGray,
      video: subVideo2,
    },
    {
      title: 'Simultaneous Heavy Equipment Location Monitoring',
      desc: 'Attach tracking tags to heavy equipment to centrally manage equipment movement routes and current locations along with workers. This reduces collision risks and improves equipment operation efficiency.',
      sub1: 'Real-Time Location Tracking',
      icon1: IconBinocularsGray,
      sub2: 'High Accuracy',
      icon2: IconRulerGray,
      video: subVideo3,
    },
    {
      title: 'Worker Route Recording and Accident Prevention',
      desc: 'Record workers’ movement paths to quickly respond in the event of an incident. Analyze risky routes in advance to support safety management plans.',
      sub1: 'Provides Statistical Data',
      icon1: IconChartBarGray,
      sub2: 'Cloud Data Storage',
      icon2: IconCloudCheckGray,
      video: subVideo4,
    },
    {
      theme: 'Record safety with data and start more efficient site operations.',
      title: 'Safety Monitoring with CCTV Integration',
      desc: 'Link real-time worker location data with site CCTV footage to immediately visualize on-site conditions. In case of emergencies, instantly check workers’ positions and surroundings via CCTV for faster and more accurate response.',
      sub1: 'Flexible Area Settings',
      icon1: IconZoneGray,
      sub2: 'Multiple Zone Configuration',
      icon2: IconTagGray,
      image: image1,
    },
    {
      title: 'Automated Safety Report Generation',
      desc: 'Daily or weekly safety reports are automatically generated and provided to managers. Accident records and prevention measures are systematically organized for efficient safety management.',
      sub1: 'Flexible Area Settings',
      icon1: IconZoneGray,
      sub2: 'Multiple Zone Configuration',
      icon2: IconTagGray,
      image: image2,
    },
    {
      title: 'Hazard Incident Data Analysis',
      desc: 'Analyze past hazard incidents and worker movement data to clearly identify high-risk areas and conditions. Use this data to improve future safety management plans.',
      sub1: 'Flexible Area Settings',
      icon1: IconZoneGray,
      sub2: 'Multiple Zone Configuration',
      icon2: IconTagGray,
      image: image3,
    },
    {
      title: 'Support for Optimized Workforce Deployment',
      desc: 'Analyze worker density and movement data to optimize workforce allocation on site. Efficient workforce management helps reduce operational costs.',
      sub1: 'Flexible Area Settings',
      icon1: IconZoneGray,
      sub2: 'Multiple Zone Configuration',
      icon2: IconTagGray,
      image: image4,
    },
  ],

  subfunc: {
    title: 'Key Feature Summary',
    desc: '',

    data: [
      {
        icon: IconLocation,
        title: 'Real-Time Worker Location Tracking',
        desc: 'Track workers’ positions in real time with ±50cm accuracy.',
      },
      {
        icon: IconWarning,
        title: 'Automatic Alerts for Restricted Area Entry',
        desc: 'Send instant alerts to managers when workers enter dangerous areas.',
      },
      {
        icon: IconCar,
        title: 'Simultaneous Heavy Equipment Monitoring',
        desc: 'Monitor heavy equipment and worker locations simultaneously to prevent collisions.',
      },
      {
        icon: IconRoute,
        title: 'Worker Route Recording',
        desc: 'Automatically record worker movement paths for hazard prevention and quick response.',
      },
      {
        icon: IconCctvLine,
        title: 'CCTV Integration',
        desc: 'Link worker locations with CCTV footage to visualize site conditions in real time.',
      },
      {
        icon: IconChartBar,
        title: 'Safety Report Generation',
        desc: 'Automatically analyze safety status and accident data to generate reports.',
      },
      {
        icon: IconSearch,
        title: 'Worker Density Management',
        desc: 'Monitor worker density in real time and alert when limits are exceeded.',
      },
      {
        icon: IconLayers,
        title: 'Optimized Workforce Deployment',
        desc: 'Analyze worker density to deploy staff efficiently.',
      },
      {
        icon: IconAnalytics,
        title: 'Hazard Data Analysis',
        desc: 'Analyze past hazard data to develop accident prevention plans.',
      },
      {
        icon: IconSecurity,
        title: 'Safety Audit and Compliance Support',
        desc: 'Use worker location and route data as audit and regulatory compliance records.',
      },
    ],
  },

  system: {
    title: 'System Configuration',
    img: rtlsSystem,
  },

  productTitle: 'Product Package',
  product: [
    {
      tag: 'workerPackage',
    },
    {
      tag: 'assetPackage',
    },
  ],

  contents: [
    {
      data: 'constructionAsset',
    },
    {
      data: 'constructionEquipment',
    },
    {
      data: 'blogRTLS',
    },
    {
      data: 'blogMedical',
    },
  ],
};
